import { CARRYFORWARD_FLOOR_ISO } from '@utazzo/shared';
import React from 'react';
import { Link } from 'react-router-dom';

import { LegacyTable, ReportSection, type LegacyCell } from '../ReportSection';

import type {
  AgentTrackerResponse,
  AgentTrackerRow,
  BookingTimeline,
  LeadPipelineResponse,
  LeadPipelineRow,
  LeadListQuery,
  LeadPivotResponse,
} from '@utazzo/shared';

export const inr = (amount: number): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString('en-IN');
  }
};

export const pct = (value: number): string => `${value.toFixed(2)}%`;
export const ratio = (part: number, whole: number): string =>
  whole > 0 ? pct((part / whole) * 100) : '0%';

/**
 * Render a count with its share of a denominator (count + a muted % below-right),
 * for the distribution tables (Lost Reasons / Booking Window / Destination /
 * Lost Stage / Lead Status). The % is shown only when both the count and the
 * denominator are positive; `denom === null` means "this is the 100% base
 * column" (a Total column), so only the count is shown. Used as the `display`
 * node of a drillCell, or as a plain cell node.
 */
export const withPct = (count: number, denom: number | null): React.ReactNode =>
  denom !== null && count > 0 && denom > 0 ? (
    <span className="tabular-nums">
      {count}
      <span className="text-muted-foreground ml-1 text-[11px] font-normal">
        {ratio(count, denom)}
      </span>
    </span>
  ) : (
    <span className="tabular-nums">{count}</span>
  );

/** A ratio plus the raw counts behind it, so a % can also show its "X of Y". */
export type RatioParts = { pct: string; num: number; den: number };
export const mkRatio = (num: number, den: number): RatioParts => ({
  pct: ratio(num, den),
  num,
  den,
});
/** "% (num/den)" cell (sorted by the ratio) for a table column. */
/** A "total" cell with its current-month / carry-forward split beneath. */
export const splitCell = (curr: number, cf: number): LegacyCell => ({
  sort: curr + cf,
  node: (
    <span className="tabular-nums">
      <span className="font-semibold">{curr + cf}</span>
      <span className="text-muted-foreground ml-1 text-[11px] font-normal">
        ({curr} / {cf})
      </span>
    </span>
  ),
});

/**
 * Clickable sibling of `splitCell`: renders the SAME "total (curr / cf)" display
 * but drills into the lead-list dialog for the COMBINED (curr + cf) pool. Used by
 * the per-agent report tables (Lead Pool / Active Breakup / Large Lead / Large
 * Lead Destination / Daily Tasks). `agentId` scopes the drill to one agent; null
 * (the Total row) drills the whole pool. Every drill is floored at the carry-
 * forward cutover (`createdAtFrom`), overridable by the caller's `query`; the
 * per-column `query` narrows the cohort. A zero total renders as a plain, inert
 * number (drillCell makes a cell clickable only when count > 0).
 */
export const splitDrillCell = (
  drillCell: (
    display: React.ReactNode,
    sortVal: number,
    count: number,
    title: string,
    query: Partial<LeadListQuery>,
  ) => LegacyCell,
  curr: number,
  cf: number,
  agentId: string | null,
  title: string,
  query: Partial<LeadListQuery>,
): LegacyCell => {
  const total = curr + cf;
  // Identical markup to `splitCell`'s node, so the clickable cell looks the same.
  const display = (
    <span className="tabular-nums">
      <span className="font-semibold">{total}</span>
      <span className="text-muted-foreground ml-1 text-[11px] font-normal">
        ({curr} / {cf})
      </span>
    </span>
  );
  return drillCell(display, total, total, title, {
    ...(agentId ? { assignedAgentId: agentId } : {}),
    createdAtFrom: CARRYFORWARD_FLOOR_ISO,
    ...query,
  });
};

export const ratioCell = (p: RatioParts): LegacyCell => ({
  node: (
    <span className="tabular-nums">
      {p.pct}
      <span className="text-muted-foreground ml-1 text-[11px] font-normal">
        ({p.num}/{p.den})
      </span>
    </span>
  ),
  sort: p.den > 0 ? p.num / p.den : 0,
});
/** "num / den" string for a KPI card hint. */
export const ratioHint = (p: RatioParts): string => `${p.num} / ${p.den}`;

/**
 * Ghosting sub-ratios rendered IDENTICALLY by the Performance KPI pair card and
 * the Contactability/Ghosting table, computed from one place so the two can
 * never drift.
 *
 * Each STAGE is a per-stage churn rate: ghosted-from-that-stage / leads that
 * ever REACHED that stage (`reachedBySubStatus[code]`). The TOTAL is total
 * ghosted / leads that ever entered an active SUB-STATUS
 * (`reachedActiveSubStatus`) - NOT `reachedActive` (the active status), because
 * leads that reached Active but never held a sub-status can't be ghosted and
 * must not inflate the base.
 *
 * These denominators differ ON PURPOSE, so Total is NOT the sum of the stages
 * and can sit just under Proposal even when Warm/Hot are 0. Reason:
 * `reachedProposal <= reachedActiveSubStatus` - a lead can enter Active via
 * Warm/Hot without ever passing through Proposal (verified in prod: Jul 2026
 * reachedProposal=260, reachedActiveSubStatus=261, reachedActive=263 - so
 * 12/260=4.62% at proposal vs 12/261=4.60% across all sub-status reachers; the
 * 2 status-only leads are correctly excluded). Both are correct; the stage rate
 * answers "of those who reached proposal, how many died there", the total
 * answers "of those who reached a working stage, how many ghosted". Full
 * precision, never the pre-rounded `ghostingPct`.
 */
export const ghostRatios = (
  r: AgentTrackerRow,
): { proposal: RatioParts; warm: RatioParts; hot: RatioParts; total: RatioParts } => ({
  proposal: mkRatio(r.ghosting.fromProposal, r.reachedBySubStatus['proposal'] ?? 0),
  warm: mkRatio(r.ghosting.fromWarm, r.reachedBySubStatus['warm'] ?? 0),
  hot: mkRatio(r.ghosting.fromHot, r.reachedBySubStatus['hot'] ?? 0),
  total: mkRatio(
    r.ghosting.fromProposal + r.ghosting.fromWarm + r.ghosting.fromHot,
    r.reachedActiveSubStatus,
  ),
});
/** Query -> Active conversion, shared by the KPI tile and the table column. */
export const queryToActive = (r: AgentTrackerRow): string => ratio(r.reachedActive, r.totalLeads);
/**
 * Contactability -> Active conversion: of the leads we actually CONTACTED
 * (`contactedCount` = a connected follow-up), how many reached Active. Isolates
 * agent quality from raw reachability - a strong ratio here means the leads an
 * agent got through to are converting, independent of how contactable the pool
 * was. Reached-Active always nests inside contacted, so this is never > 100%.
 */
export const contactabilityToActive = (r: AgentTrackerRow): string =>
  ratio(r.reachedActive, r.contactedCount);
/**
 * WhatsApp -> Active ratio: of the leads that reached Active, how many did so
 * because the customer ENGAGED on WhatsApp - a chat or a reply to a template
 * message - on or before going active (`whatsAppActiveCount`). Isolates how much
 * of the active pipeline WhatsApp is driving vs phone-only. Numerator nests
 * inside reachedActive, so never > 100%.
 */
export const whatsAppToActive = (r: AgentTrackerRow): string =>
  ratio(r.whatsAppActiveCount, r.reachedActive);
/** Avg published itinerary versions per lead that has an itinerary (all leads /
 *  WON leads). Kept as sum/count so the totals row is a correct weighted mean. */
export const avgVersions = (r: AgentTrackerRow): string =>
  r.itineraryVersionsLeadCount > 0
    ? (r.itineraryVersionsSum / r.itineraryVersionsLeadCount).toFixed(2)
    : '-';
export const avgVersionsWon = (r: AgentTrackerRow): string =>
  r.wonItineraryVersionsLeadCount > 0
    ? (r.wonItineraryVersionsSum / r.wonItineraryVersionsLeadCount).toFixed(2)
    : '-';
/** Format an average duration (Σ seconds / count) as "Xd Yh" / "Yh" / "Zm". */
export const avgDuration = (secondsSum: number, count: number): string => {
  if (count <= 0) return '-';
  const avgSec = secondsSum / count;
  if (avgSec < 3600) return `${Math.max(1, Math.round(avgSec / 60))}m`;
  const hours = avgSec / 3600;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const days = Math.floor(hours / 24);
  const remH = Math.round(hours - days * 24);
  return `${days}d ${remH}h`;
};
/** Colour an avg-duration cell by SLA: <5h green, 5h-1d amber, >1d red.
 *  Returns a plain '-' (uncoloured) when there is no data. */
export const durationCell = (secondsSum: number, count: number): LegacyCell => {
  const text = avgDuration(secondsSum, count);
  if (count <= 0) return text;
  const avgSec = secondsSum / count;
  const tone =
    avgSec < 5 * 3600
      ? 'text-emerald-600'
      : avgSec <= 24 * 3600
        ? 'text-amber-600'
        : 'text-red-600';
  return { node: <span className={`${tone} font-medium`}>{text}</span>, sort: avgSec };
};
/** Avg time from a lead first going active to the first customer-facing quote. */
export const avgTimeToFirstQuote = (r: AgentTrackerRow): LegacyCell =>
  durationCell(r.firstQuoteSecondsSum, r.firstQuoteCount);
/** Avg gap between consecutive itinerary versions sent (no SLA colour). */
export const avgVersionGap = (r: AgentTrackerRow): string =>
  avgDuration(r.versionGapSecondsSum, r.versionGapCount);
/** Lost Trustability = approved / (approved + rejected) lost-approval requests
 *  raised this month; '-' when nothing has been decided yet. */
export const lostTrustability = (r: AgentTrackerRow): string => {
  const decided = r.lostApproved + r.lostRejected;
  return decided > 0 ? ratio(r.lostApproved, decided) : '-';
};
/** Whether an agent row has ANY reporting activity for the selected month.
 *  Rows with everything zero (inactive users the roster seeds for the team
 *  hierarchy) are hidden from the tables unless they parent an active member. */
export const rowHasData = (r: AgentTrackerRow): boolean =>
  r.totalLeads > 0 ||
  r.carriedInLeads > 0 ||
  (r.target?.amount ?? 0) > 0 ||
  r.wonTtvAmount > 0 ||
  r.lostClosedCount > 0 ||
  r.itineraryVersionsLeadCount > 0 ||
  r.lostApproved + r.lostRejected > 0 ||
  r.largeLeadStats.openCurrent + r.largeLeadStats.openCarryFwd > 0 ||
  r.liveFollowUps.todayFollowUp +
    r.liveFollowUps.todayFirstContact +
    r.liveFollowUps.missedFollowUp +
    r.liveFollowUps.missedFirstContact +
    r.liveFollowUps.revertPending >
    0;

// Real ACTIVITY in the period (outcomes, not just holding stale open leads).
// Used to keep a *deactivated* agent visible only when they actually did
// something this period (created leads, won/lost, published versions, or raised
// a lost-approval) - e.g. someone deactivated mid-month who still had losses.
// It deliberately excludes carried-in / open-lead / live-follow-up signals,
// which is how a deactivated agent left holding stale leads slips past
// `rowHasData`.
export const rowHasPeriodActivity = (r: AgentTrackerRow): boolean =>
  r.totalLeads > 0 ||
  r.wonTtvAmount > 0 ||
  r.lostClosedCount > 0 ||
  r.itineraryVersionsLeadCount > 0 ||
  r.lostApproved + r.lostRejected > 0;

// Whether an agent's row should appear in the report tables at all. Active
// agents follow the usual "has any data" rule; deactivated agents only show if
// they had real activity this period, so a deactivated agent holding stale open
// leads (0 created / 0 closed) drops out of every table.
export const isRowVisible = (r: AgentTrackerRow): boolean =>
  r.isActive ? rowHasData(r) : rowHasPeriodActivity(r);

// ----- Lead Status tab helpers -----
// Active sub-status -> the carriedIn breakdown fields for that stage, so the
// combined ("current month + carry forward") counts pull from BOTH pools.
export const CARRY_CURRENT: Record<string, keyof AgentTrackerRow['carriedIn']> = {
  proposal: 'currentProposal',
  warm: 'currentWarm',
  hot: 'currentHot',
};
export const CARRY_MTD: Record<string, keyof AgentTrackerRow['carriedIn']> = {
  proposal: 'mtdProposal',
  warm: 'mtdWarm',
  hot: 'mtdHot',
};
export const CARRY_REACHED: Record<string, keyof AgentTrackerRow['carriedIn']> = {
  proposal: 'reachedProposal',
  warm: 'reachedWarm',
  hot: 'reachedHot',
};
/** Combined "currently sitting in sub-status X" = month cohort + carried-in. */
export const subCurrent = (r: AgentTrackerRow, code: string): number => {
  const k = CARRY_CURRENT[code];
  return (r.bySubStatus[code] ?? 0) + (k ? r.carriedIn[k] : 0);
};
/** Combined "entered sub-status X this month" (MTD) = month reached + carried MTD. */
export const subMtd = (r: AgentTrackerRow, code: string): number => {
  const k = CARRY_MTD[code];
  return (r.reachedBySubStatus[code] ?? 0) + (k ? r.carriedIn[k] : 0);
};
/** Combined "ever reached sub-status X" = month-cohort reached (history) +
 *  carried-cohort reached (current funnel position / history). Always >= CUR. */
export const subReached = (r: AgentTrackerRow, code: string): number => {
  const k = CARRY_REACHED[code];
  return (r.reachedBySubStatus[code] ?? 0) + (k ? r.carriedIn[k] : 0);
};
/** Combined open large leads = this month's + carried forward. */
export const largeCombined = (r: AgentTrackerRow): number =>
  r.largeLeadStats.openCurrent + r.largeLeadStats.openCarryFwd;
/**
 * Cohort-correct funnel ratios: each numerator is "reached BOTH stages"
 * (`funnelReached`), each denominator is "reached the earlier stage", so a
 * ratio can never exceed 100% (a lead that won without reaching Hot is not in
 * `hotAndWon`). Shared by the funnel KPI pair card and the table columns.
 */
export const funnelRatios = (
  r: AgentTrackerRow,
): {
  propToWarm: RatioParts;
  warmToHot: RatioParts;
  hotToWon: RatioParts;
  propToLoss: RatioParts;
  warmToLoss: RatioParts;
  hotToLoss: RatioParts;
} => ({
  propToWarm: mkRatio(r.funnelReached.proposalAndWarm, r.reachedBySubStatus['proposal'] ?? 0),
  warmToHot: mkRatio(r.funnelReached.warmAndHot, r.reachedBySubStatus['warm'] ?? 0),
  hotToWon: mkRatio(r.funnelReached.hotAndWon, r.reachedBySubStatus['hot'] ?? 0),
  propToLoss: mkRatio(r.funnelReached.proposalAndLost, r.reachedBySubStatus['proposal'] ?? 0),
  warmToLoss: mkRatio(r.funnelReached.warmAndLost, r.reachedBySubStatus['warm'] ?? 0),
  hotToLoss: mkRatio(r.funnelReached.hotAndLost, r.reachedBySubStatus['hot'] ?? 0),
});

/** Pipeline leads split into all-files vs the realised ("on the dot") subset. */
export type Bucketed = { files: LeadPipelineRow[]; realised: LeadPipelineRow[] };
export const EMPTY_BUCKET: Bucketed = { files: [], realised: [] };

export const TIMELINE_BUCKETS: Array<{ code: BookingTimeline; label: string }> = [
  { code: 'WITHIN_7_DAYS', label: '0 - 7 Days' },
  { code: 'EIGHT_TO_15_DAYS', label: '08 - 15 Days' },
  { code: 'SIXTEEN_TO_30_DAYS', label: '16 - 30 Days' },
  { code: 'ONE_TO_THREE_MONTHS', label: '1 - 3 Months' },
  { code: 'MORE_THAN_THREE_MONTHS', label: 'Greater than 3 Months' },
];

export const lostAt = (row: AgentTrackerRow, code: string): { count: number; followUps: number } =>
  row.lostAtSubStatus[code] ?? { count: 0, followUps: 0 };

/** Column with the highest total in a pivot, e.g. the top lost reason. */
export const topPivotColumn = (pivot: LeadPivotResponse | undefined): string => {
  if (!pivot || pivot.grandTotal === 0 || pivot.columns.length === 0) return '-';
  const max = Math.max(...pivot.columnTotals);
  return pivot.columns[pivot.columnTotals.indexOf(max)] ?? '-';
};

/** Per-day realised pipeline rows, computed like the legacy buildTableData. */
/** ISO date/timestamp -> DD-MM-YYYY (no timezone shift; slices the date part). */
export const dmy = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}-${m}-${y}` : '-';
};

export const PipelineLeadsTable = ({
  title,
  pipeline,
  loading,
  withTotals = false,
  compact = false,
}: {
  title: string;
  pipeline: LeadPipelineResponse | undefined;
  loading: boolean;
  withTotals?: boolean;
  /** Drop TTV / Destination / Status (Today's Pipeline shows only the first 7 columns). */
  compact?: boolean;
}): React.JSX.Element => {
  const colCount = compact ? 7 : 10;
  return (
    <ReportSection title={title}>
      <LegacyTable
        loading={loading || !pipeline}
        emptyText="No leads to display."
        headers={[
          'Agent',
          'Marked Hot',
          'Payment Date',
          'Booking Date',
          'Client Name',
          'Email',
          'Phone',
          'TTV',
          'Destination',
          'Status',
        ].slice(0, colCount)}
        rows={(pipeline?.rows ?? []).map((row) =>
          [
            row.agentName ?? 'N/A',
            { node: dmy(row.markedHotAt), sort: row.markedHotAt?.slice(0, 10) ?? '' },
            { node: dmy(row.expectedPaymentDate), sort: row.expectedPaymentDate ?? '' },
            { node: dmy(row.bookingDate), sort: row.bookingDate ?? '' },
            {
              node: (
                <Link
                  key={row.leadId}
                  to={`/leads/${row.leadId}`}
                  className="text-foreground font-medium underline-offset-4 hover:underline"
                >
                  {row.customerName ?? row.reference}
                </Link>
              ),
              sort: row.customerName ?? row.reference,
            },
            row.customerEmail ?? 'N/A',
            row.customerPhone ?? 'N/A',
            inr(row.ttvAmount ?? 0),
            row.destination ?? 'N/A',
            row.statusName,
          ].slice(0, colCount),
        )}
        totals={
          withTotals && pipeline
            ? [
                `Total ${pipeline.count}`,
                '',
                '',
                '',
                '',
                '',
                '',
                inr(pipeline.totalTtvAmount),
                '',
                '',
              ].slice(0, colCount)
            : undefined
        }
      />
    </ReportSection>
  );
};

/**
 * One rendered table row in the master-child agent view: SALES_LEAD rows are
 * masters (depth 0, expandable) and their direct reports render indented
 * (depth 1) right below once expanded.
 */
export interface AgentEntry {
  row: AgentTrackerRow;
  depth: 0 | 1;
  childCount: number;
}

/**
 * Narrow agent-table entries to rows that carry data for a specific table,
 * without breaking the TL -> agent hierarchy. A standalone agent or a child
 * row survives only when `hasData(row)` is true; a reporting manager (a
 * depth-0 row with children) survives when they OR any of their direct reports
 * has data, so an otherwise-empty manager still anchors their non-empty team.
 * `report` is needed because a COLLAPSED manager's children are absent from
 * `entries`, yet the manager must still show when a hidden report has data.
 * Used by low-density tables (Large Lead, Large Lead Destination) where the
 * all-zero rows are pure noise.
 */
export const filterEntriesByRowData = (
  entries: AgentEntry[],
  report: AgentTrackerResponse | undefined,
  hasData: (r: AgentTrackerRow) => boolean,
): AgentEntry[] => {
  const managersWithData = new Set<string>();
  for (const r of report?.rows ?? []) {
    if (r.reportingManagerId && r.agentId !== r.reportingManagerId && hasData(r)) {
      managersWithData.add(r.reportingManagerId);
    }
  }
  return entries.filter(({ row, depth, childCount }) => {
    if (hasData(row)) return true;
    if (depth === 0 && childCount > 0 && row.agentId) {
      return managersWithData.has(row.agentId);
    }
    return false;
  });
};

export const wonCount = (report: AgentTrackerResponse | undefined, row: AgentTrackerRow): number =>
  report
    ? report.statuses
        .filter((status) => status.bucket === 'WON')
        .reduce((sum, status) => sum + (row.byStatus[status.code] ?? 0), 0)
    : 0;
// Month-created leads currently in a LOST status. NOT `Σ lostAtSubStatus`,
// which only covers losses OUT of an active sub-status (proposal/warm/hot) -
// most leads are lost from not_qualified (no sub-status) and would be missed.
export const lostCount = (
  report: AgentTrackerResponse | undefined,
  row: AgentTrackerRow,
): number =>
  report
    ? report.statuses
        .filter((status) => status.bucket === 'LOST')
        .reduce((sum, status) => sum + (row.byStatus[status.code] ?? 0), 0)
    : 0;

export const num = (n: number): string => n.toLocaleString('en-IN');

export const days = (v: number | null | undefined): string =>
  v !== null && v !== undefined ? `${v}d` : '-';

// Maps a lead status code to its carried-in field, so the pool cards can sum
// the month cohort + the carried-in cohort into one "Total" per status.
export const CARRY_STATUS_FIELD: Record<string, keyof AgentTrackerRow['carriedIn']> = {
  not_qualified: 'currentNq',
  active: 'currentActive',
  lost: 'currentLost',
  won: 'currentWon',
  long_followup: 'currentLongFollowup',
};

/**
 * Destination-pivot cell renderer -> drills to the leads created this period for
 * that agent + destination. `rowIndex` null = totals row (all agents),
 * `colIndex` null = Total column (all destinations); the "Unspecified" bucket
 * drills to leads with no destination. `extraQuery` narrows the drill (e.g. the
 * large-lead cohort).
 */
export const makeDestinationPivotCell =
  (
    pivot: LeadPivotResponse | undefined,
    drillCell: (
      display: React.ReactNode,
      sortVal: number,
      count: number,
      title: string,
      query: Partial<LeadListQuery>,
    ) => LegacyCell,
    from: string,
    to: string,
    extraQuery: Partial<LeadListQuery> = {},
  ) =>
  (count: number, rowIndex: number | null, colIndex: number | null): LegacyCell => {
    if (!pivot) return count;
    // colIndex null = the Total column (all destinations). Otherwise a uuid for
    // a named destination, or null for the "Unspecified" bucket (no destination).
    const destId = colIndex === null ? undefined : (pivot.columnIds[colIndex] ?? null);
    const agentId = rowIndex === null ? null : (pivot.rowIds[rowIndex] ?? null);
    const who = rowIndex === null ? 'All agents' : pivot.rows[rowIndex];
    const where = colIndex === null ? 'all destinations' : pivot.columns[colIndex];
    // % = this destination's share of the row total (an agent's leads, or the
    // grand total on the Total row). The Total column is the 100% base -> no %.
    const denom =
      colIndex === null
        ? null
        : rowIndex === null
          ? pivot.grandTotal
          : (pivot.rowTotals[rowIndex] ?? 0);
    return drillCell(withPct(count, denom), count, count, `${who} - ${where}`, {
      ...(agentId ? { assignedAgentId: agentId } : {}),
      ...(destId
        ? { destinationId: destId }
        : destId === null
          ? { destinationUnspecified: true }
          : {}),
      createdAtFrom: from,
      createdAtTo: to,
      ...extraQuery,
    });
  };
